import { useEffect, useRef, useState, type RefObject } from 'react'
import { ActionType, type Chart, type Crosshair, type DomPosition } from 'klinecharts'
import type { EventBus } from '../core/EventBus'
import { CANDLE_PANE_ID } from '../types/chart'
import { CHART_FONT_FAMILY } from '../utils/chartColors'
import {
  PLUS_BUTTON,
  isClearedCrosshair,
  keepsLatch,
  plusPlacement,
  plusTarget,
  type AddPriceAlertRequest,
  type PlusTarget,
} from '../utils/crosshairPriceAction'

export interface CrosshairPriceActionProps {
  chart: Chart | null
  /** ChartManager, narrowed to what the "+" reads. */
  manager: { isDrawingPending(): boolean; clearCrosshair(): void } | null
  eventBus: EventBus | null
  axisOnLeft: boolean
  /** The crosshair label's resolved background, so the two read as one piece. */
  labelBg: string
  /** The chart canvas container: a right click on the button is replayed there. */
  containerRef: RefObject<HTMLElement | null>
  onAdd: (request: AddPriceAlertRequest) => void
}

/**
 * The "+" joined to the crosshair price label (chart crosshair alert spec, B1). Clicking it
 * asks the host for its price alert popover at that price.
 *
 * Moving toward the button clears klinecharts' crosshair (the axis, KC:12743-12746; any DOM
 * element over the canvas, KC:12938-12941), so the "+" latches at its last price until the
 * chart's crosshair:leave or until the pointer drifts off its row. While the pointer is on
 * it, the label is held at that price. Synced peers never show it: their crosshair arrives
 * through executeAction, which fires no action (KC:14027-14035). Renders nothing when idle.
 */
export function CrosshairPriceAction({
  chart,
  manager,
  eventBus,
  axisOnLeft,
  labelBg,
  containerRef,
  onAdd,
}: CrosshairPriceActionProps) {
  const [target, setTarget] = useState<PlusTarget | null>(null)
  const targetRef = useRef(target)
  targetRef.current = target
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!chart) return
    const priceAt = (y: number) => {
      const p = chart.convertFromPixel([{ y }], { paneId: CANDLE_PANE_ID })
      return (Array.isArray(p) ? p[0] : p)?.value
    }
    const onCrosshair = (c: Crosshair) => {
      // klinecharts clears its crosshair on the axis without running this action (KC:12743-12746,
      // 13569-13571), so this is only a guard; the latch is released by the drift test below.
      if (isClearedCrosshair(c)) return
      const next = plusTarget({
        crosshair: c,
        drawing: manager?.isDrawingPending() ?? false,
        priceAt,
        precision: chart.getPriceVolumePrecision().price,
      })
      setTarget((prev) =>
        prev && next && prev.y === next.y && prev.text === next.text && prev.x === next.x
          ? prev
          : next,
      )
    }
    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    const offLeave = eventBus?.on('crosshair:leave', () => setTarget(null))
    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      offLeave?.()
    }
  }, [chart, manager, eventBus])

  // While latched, moving up or down off the button's row lets go. klinecharts never tells us
  // its crosshair was cleared (see onCrosshair), so this runs whenever a target exists. It only
  // judges the axis strip and the button's own width: in the pane the crosshair is live and
  // the target follows it, but pointermove fires before klinecharts' mousemove, so a fast
  // vertical move there would null the target for a frame. Read through targetRef so the
  // listener is bound once per latch, not on every crosshair move.
  const latched = target != null
  useEffect(() => {
    if (!latched) return
    const onMove = (e: PointerEvent) => {
      const t = targetRef.current
      const host = buttonRef.current?.parentElement
      const axis = chart?.getSize(CANDLE_PANE_ID, 'yAxis' as DomPosition)
      if (!t || !host || !axis) return
      const rect = host.getBoundingClientRect()
      const x = e.clientX - rect.left
      const onStrip = axisOnLeft
        ? x <= axis.left + axis.width + PLUS_BUTTON.width
        : x >= axis.left - PLUS_BUTTON.width
      if (onStrip && !keepsLatch(t, e.clientY - rect.top)) setTarget(null)
    }
    document.addEventListener('pointermove', onMove)
    return () => document.removeEventListener('pointermove', onMove)
  }, [latched, chart, axisOnLeft])

  if (!target || !chart) return null
  // The literal matches DomPosition.YAxis; a type-only import keeps the mocked runtime out.
  const place = plusPlacement({
    y: target.y,
    axis: chart.getSize(CANDLE_PANE_ID, 'yAxis' as DomPosition) ?? null,
    axisOnLeft,
  })
  if (!place) return null
  const label = `Add alert at $${target.text}`
  return (
    <button
      ref={buttonRef}
      type="button"
      data-testid="crosshair-price-action"
      // Not an outside click: a selected drawing stays selected (ChartManager.ts:1250-1260).
      data-chart-chrome=""
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.stopPropagation()}
      // mouseover, not mouseenter: React raises onMouseEnter from the native mouseout, which
      // comes before klinecharts' root mouseleave clears the crosshair (KC:12402, 12938), so
      // a hold there would be wiped at once. Idempotent: the same coordinates repaint nothing.
      onMouseOver={() => {
        const t = targetRef.current
        if (!t) return
        chart.executeAction(ActionType.OnCrosshairChange, {
          x: t.x ?? undefined,
          y: t.y,
          paneId: CANDLE_PANE_ID,
        })
      }}
      onMouseLeave={() => manager?.clearCrosshair()}
      // The button covers a strip of the pane, outside the container's own contextmenu
      // listener (EdgeChart): replay the right click there so the menus, the alert cancel and
      // the drawing check run at this point instead of the browser's native menu.
      onContextMenu={(e) => {
        e.preventDefault()
        containerRef.current?.dispatchEvent(
          new MouseEvent('contextmenu', {
            clientX: e.clientX,
            clientY: e.clientY,
            bubbles: true,
            cancelable: true,
          }),
        )
      }}
      onClick={(e) => {
        e.stopPropagation()
        const t = targetRef.current
        if (!t) return
        const r = e.currentTarget.getBoundingClientRect()
        onAdd({
          price: t.price,
          anchorRect: { left: r.left, top: r.top, width: r.width, height: r.height },
          axisSide: axisOnLeft ? 'left' : 'right',
        })
      }}
      className={
        'absolute z-20 flex items-center justify-center text-white ' +
        // A finger needs more than 15px: a larger invisible target on coarse pointers.
        "after:absolute after:inset-0 after:content-[''] pointer-coarse:after:-inset-3 " +
        // Squared on the side that touches the label.
        (axisOnLeft ? 'rounded-r-sm' : 'rounded-l-sm')
      }
      style={{
        left: place.left,
        top: place.top,
        width: PLUS_BUTTON.width,
        height: PLUS_BUTTON.height,
        background: labelBg,
        fontFamily: CHART_FONT_FAMILY,
      }}
    >
      <svg
        width={9}
        height={9}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        aria-hidden
        className="pointer-events-none"
      >
        <path d="M5 12h14" />
        <path d="M12 5v14" />
      </svg>
    </button>
  )
}
